/**
 * Service d'authentification pour l'application.
 *
 * Fournit des méthodes pour :
 * - login : authentifier un utilisateur avec email/téléphone et mot de passe.
 * - getMeInfo : récupérer les informations de l'utilisateur connecté.
 *
 * Utilise Axios pour effectuer les requêtes HTTP.
 * axiosInstance inclut les interceptors pour gérer automatiquement les tokens ou erreurs.
 *
 * Types :
 * - ILoginBody : structure attendue pour les informations de connexion.
 * - AxiosError : type utilisé pour gérer les erreurs des requêtes HTTP.
 *
 * BASE_URL est récupéré depuis les variables d'environnement.
 */
import type { AxiosError } from 'axios';
import axios from 'axios';
import axiosInstance from '@/services/interceptor.services';
import type { ILoginBody } from '@/types/auth.types';
import { VITE_API_URL as BASE_URL } from '@/utils/runtimeConfig';

export const AuthService = {
  /**
   * Authentifie un utilisateur avec ses identifiants.
   * @param data - Informations de connexion (ILoginBody)
   * @returns Promise contenant la réponse de l'API ou une erreur Axios
   */
  login: async (data: ILoginBody) => {
    try {
      const response = axios.post(`${BASE_URL}auth/login.staff`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Initie une demande de réinitialisation de mot de passe (envoi OTP par email).
   */
  initResetPassword: async (data: { email: string }) => {
    try {
      const response = await axiosInstance.put('auth/reset-password/init', {
        email: data.email,
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Confirme la réinitialisation du mot de passe via le code OTP.
   */
  confirmResetPassword: async (data: {
    email: string;
    otp: string;
    newPassword: string;
  }) => {
    try {
      const response = await axiosInstance.put('auth/reset-password/confirm', {
        email: data.email,
        otp: data.otp,
        newPassword: data.newPassword,
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * Récupère les informations de l'utilisateur actuellement connecté.
   * @returns Données de l'utilisateur si succès, sinon réponse d'erreur Axios
   */
  getMeInfo: async () => {
    try {
      const response = await axiosInstance.get<any>('auth/me');
      if (response.data) {
        return response.data;
      }
    } catch (error) {
      const err = error as AxiosError;
      return err?.response;
    }
  },
};
